'use client';

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { Box, VStack } from '@terra/ui-styled-system';
import { BodyDefault, Input, LabelDefault } from '@terra/ui-design-system';
import { FolderMinusIcon, Delete02Icon } from '@terra/ui-icons';
import { type TabGroup, type TabGroupColor } from '../../../store/layoutStore';
import { KeyboardKey } from '../../../constants/keyboardKeys';
import { TabGroupColorPicker } from './TabGroupColorPicker';
import {
  getDecorativeBgColor,
  getDecorativeBorderColor,
  getDecorativeFgColor,
} from './tabGroupDecorativeColor';

const NO_DRAG = { WebkitAppRegion: 'no-drag' } as React.CSSProperties;
const EDIT_MENU_GUTTER_PX = 4;
const EDIT_MENU_WIDTH_PX = 240;

type TabGroupHeaderProps = {
  group: TabGroup;
  isEditingOpen: boolean;
  startWithEmptyName: boolean;
  onEditingOpenChange: (open: boolean) => void;
  onToggleCollapse: () => void;
  onRenameGroup: (name: string) => void;
  onChangeColor: (color: TabGroupColor) => void;
  onUngroup: () => void;
  onDeleteGroup: () => void;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
};

export function TabGroupHeader({
  group,
  isEditingOpen,
  startWithEmptyName,
  onEditingOpenChange,
  onToggleCollapse,
  onRenameGroup,
  onChangeColor,
  onUngroup,
  onDeleteGroup,
  isDragging,
  isDragOver,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
}: TabGroupHeaderProps) {
  const t = useTranslations('layout');
  const labelRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [draftName, setDraftName] = useState(
    startWithEmptyName ? '' : group.name,
  );
  const commitName = useCallback(() => {
    const trimmed = draftName.trim();

    if (trimmed && trimmed !== group.name) {
      onRenameGroup(trimmed);
    }
  }, [draftName, group.name, onRenameGroup]);
  const closeMenu = useCallback(() => {
    commitName();
    onEditingOpenChange(false);
  }, [commitName, onEditingOpenChange]);

  useEffect(() => {
    if (isEditingOpen) {
      setDraftName(startWithEmptyName ? '' : group.name);
    }
  }, [isEditingOpen, startWithEmptyName, group.name]);
  useLayoutEffect(() => {
    if (!isEditingOpen) {
      setMenuPosition(null);

      return;
    }

    const updatePosition = () => {
      const label = labelRef.current;

      if (!label) return;
      const shell = label.closest('[data-tab-group-shell]');
      const rect = (shell ?? label).getBoundingClientRect();

      setMenuPosition({
        top: Math.round(rect.bottom + EDIT_MENU_GUTTER_PX),
        left: Math.round(rect.left),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isEditingOpen]);
  useEffect(() => {
    if (!isEditingOpen) return;
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target;

      if (!(target instanceof Node)) return;
      if (labelRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeMenu();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === KeyboardKey.Escape) {
        onEditingOpenChange(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isEditingOpen, closeMenu, onEditingOpenChange]);
  const editMenu =
    isEditingOpen && menuPosition
      ? createPortal(
          <Box
            ref={menuRef}
            position="fixed"
            zIndex="popover"
            w={`${EDIT_MENU_WIDTH_PX}px`}
            p="2"
            borderRadius="xl"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="bg.default"
            boxShadow="md"
            style={{
              top: menuPosition.top,
              left: menuPosition.left,
            }}
          >
            <VStack gap="1" alignItems="stretch">
              <Input
                size="sm"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder={t('nameThisGroup')}
                autoFocus
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === KeyboardKey.Enter) {
                    closeMenu();
                  }

                  if (e.key === KeyboardKey.Escape) {
                    onEditingOpenChange(false);
                  }
                }}
              />
              <TabGroupColorPicker
                selectedColor={group.color}
                onSelectColor={onChangeColor}
              />
              <Box h="1px" bg="border.subtle" my="0.5" />
              <Box
                display="flex"
                alignItems="center"
                gap="2"
                px="2"
                py="2"
                borderRadius="sm"
                cursor="pointer"
                _hover={{ bg: 'bg.neutral.hover' }}
                onClick={() => {
                  onUngroup();
                  onEditingOpenChange(false);
                }}
              >
                <FolderMinusIcon size={14} strokeWidth={1.75} />
                <BodyDefault>{t('ungroup')}</BodyDefault>
              </Box>
              <Box
                display="flex"
                alignItems="center"
                gap="2"
                px="2"
                py="2"
                borderRadius="sm"
                cursor="pointer"
                _hover={{ bg: 'bg.neutral.hover' }}
                onClick={() => {
                  onDeleteGroup();
                  onEditingOpenChange(false);
                }}
              >
                <Delete02Icon size={14} strokeWidth={1.75} />
                <BodyDefault>{t('deleteGroup')}</BodyDefault>
              </Box>
            </VStack>
          </Box>,
          document.body,
        )
      : null;

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      alignSelf="center"
      flexShrink={0}
      style={NO_DRAG}
      draggable={!isEditingOpen}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', group.id);
        onDragStart(e);
      }}
      onDragEnd={onDragEnd}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDragOver(e);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onDrop(e);
      }}
    >
      <Box
        ref={labelRef}
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        h="5"
        borderRadius="sm"
        borderWidth="1px"
        borderStyle="solid"
        p="0.5"
        cursor={isDragging ? 'grabbing' : 'pointer'}
        opacity={isDragging ? 0.5 : 1}
        transform={isDragOver ? 'scale(1.02)' : undefined}
        transition="transform 0.2s, opacity 0.2s"
        role="button"
        tabIndex={0}
        aria-label={group.isCollapsed ? t('expandGroup') : t('collapseGroup')}
        aria-expanded={!group.isCollapsed}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isEditingOpen) {
            onToggleCollapse();
          }
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onEditingOpenChange(true);
        }}
        onKeyDown={(e) => {
          if (e.key === KeyboardKey.Enter || e.key === KeyboardKey.Space) {
            e.preventDefault();
            onToggleCollapse();
          }
        }}
        style={{
          backgroundColor: getDecorativeBgColor(group.color),
          borderColor: getDecorativeBorderColor(group.color),
          color: getDecorativeFgColor(group.color),
        }}
      >
        <LabelDefault
          fontWeight="medium"
          truncate
          maxW="120px"
          color="inherit"
          whiteSpace="nowrap"
          textAlign="center"
        >
          {group.name}
        </LabelDefault>
      </Box>
      {editMenu}
    </Box>
  );
}
